import { Box, Button } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { FinanceValueObjectsInvoiceStatusEnum, InvoiceFragment } from 'generated/resources/graphql'
import moment from 'moment'

import Footer from 'modules/core/components/Sidepanel/Footer'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { canUpdateInvoiceData, isInvoiceEligibleToSendToPay } from 'modules/finance/lib/invoices'

import { InvoiceForm } from './types'

export const FooterActions = ({
  form,
  disableActions,
  invoice,
  showSendToPayButton,
}: {
  form: FormikProps<InvoiceForm>
  disableActions: boolean
  invoice: InvoiceFragment
  showSendToPayButton: boolean
}) => {
  const editableInvoice = canUpdateInvoiceData(invoice)
  const paidInvoice = invoice.status == 'paid'
  const sentToPayInvoice = invoice.status == 'sent_to_pay'

  const onSubmitWithStatus = (status: FinanceValueObjectsInvoiceStatusEnum) => {
    form.setValues({
      ...form.values,
      status: status,
    })

    form.handleSubmit()
  }

  const onSubmitValidated = () => {
    form.setValues({
      ...form.values,
      validatedAt: moment().toISOString(),
    })
    form.handleSubmit()
    track(trackingEvents.accounting.invoices.validate)
  }

  const onSubmitValidatedAndPaid = () => {
    form.setValues({
      ...form.values,
      validatedAt: moment().toISOString(),
      status: 'paid',
    })
    form.handleSubmit()
    track(trackingEvents.accounting.invoices.validateAndMarkAsPaid)
  }

  const trackSaveAction = () => {
    const modifiedInputs = Object.keys(form.values).filter(
      (key) =>
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        form.values[key as keyof InvoiceForm] !== form.initialValues[key as keyof InvoiceForm]
    )
    track(trackingEvents.accounting.invoices.saveBasicInformation, {
      modified_inputs: modifiedInputs,
    })
  }

  const SaveButton = () => (
    <Button
      disabled={disableActions}
      onClick={() => {
        onSubmitWithStatus(invoice.status)
        trackSaveAction()
      }}
      type="secondary"
    >
      {i18n.t('expenses_v2.buttons.save')}
    </Button>
  )

  const PaidButton = () => (
    <Button type="primary" disabled={disableActions} onClick={() => onSubmitWithStatus('paid')}>
      {i18n.t('finance.invoices.actions.mark_as_paid')}
    </Button>
  )

  const SendToPayButton = () => (
    <Button
      type="secondary"
      disabled={disableActions}
      onClick={() => onSubmitWithStatus('sent_to_pay')}
    >
      {i18n.t('finance.invoices.actions.send_to_pay_ok')}
    </Button>
  )

  const ValidateButton = () => {
    if (!paidInvoice && !sentToPayInvoice) {
      return (
        <Button.Split
          type="positive"
          label={i18n.t('finance.invoices.actions.validate')}
          disabled={disableActions}
          onClick={() => onSubmitValidated()}
        >
          <Button.Split.Option key={'approve_mark_paid'} onClick={() => onSubmitValidatedAndPaid()}>
            {i18n.t('finance.invoices.actions.validate_and_mark_as_paid')}
          </Button.Split.Option>
        </Button.Split>
      )
    } else {
      return (
        <Button type="primary" disabled={disableActions} onClick={() => onSubmitValidated()}>
          {i18n.t('finance.invoices.actions.validate')}
        </Button>
      )
    }
  }

  if (!editableInvoice && (sentToPayInvoice || paidInvoice)) return <></>

  return (
    <Box
      position="fixed"
      style={{
        bottom: '0',
        width: '550px',
      }}
    >
      <Footer
        content={
          <Box
            width="full"
            flexDirection="row"
            justifyContent={'spaceBetween'}
            alignItems="center"
            paddingX="s8"
            gap="s8"
          >
            <Box flexDirection="row" justifyContent="flexEnd" alignItems="center" gap="s8">
              {editableInvoice && <SaveButton />}
            </Box>

            <Box flexDirection="row" justifyContent="flexEnd" alignItems="center" gap="s8">
              {editableInvoice && <ValidateButton />}
              {!editableInvoice && !sentToPayInvoice && !paidInvoice && <PaidButton />}
              {isInvoiceEligibleToSendToPay(invoice) && showSendToPayButton && <SendToPayButton />}
            </Box>
          </Box>
        }
      />
    </Box>
  )
}
